/**
 * The purchase-order reads (card AIW-PO).
 *
 * The screens ask `/api/purchase-orders` and `/api/purchase-orders/:po`; the API
 * reads `public.pos` (what the platform raised) and `supply.po` (what the CRM
 * raises) and tags each row with its source, because the two carry different
 * fields — the legacy one has no promised date and no acknowledgement at all.
 */
import {authHeaders, fetchImpl} from './api';
import {env} from './env';
import {PurchaseOrderDefaults} from '@aiw/config';

export type PoVerification = {state: 'covers' | 'short' | 'over' | 'unknown'; required: number | null; ordered: number | null; percentOver: number | null};

export type PoListRow = {
  po: string; key:string; pickup_references:string[]; provisional_references:string[]; evidence_conflict:boolean; ready:boolean; collected:boolean; source: 'platform' | 'crm'; job_ref: string | null; 
  /** public.suppliers.id (supplier hub, 16 Oct 2026); optional so older mock rows still type */
  supplier_id?: string | null;job_name: string | null;
  job_stage: string | null; suburb: string | null; supplier: string | null; supplier_short: string | null;
  status: string; sent_at: string | null; acknowledged_at: string | null; delivered_at: string | null;
  promised_date: string | null; lines: number; total_ex_gst: number | null; material_must_land: boolean;
  next_install: string | null; no_amount: boolean;
  /** Jacky, 5 October 2026: one plain status per order (see services/api/src/purchase-orders.ts). */
  group: 'needs_job' | 'to_send' | 'on_the_way' | 'done';
  confirmed: boolean; credit_terms: boolean | null;
  suggested_job_ref: string | null; suggested_job_name: string | null; suggestion_note: string | null;
  /** Every Job the order serves; one supplier order can cover several (13 October 2026). */
  job_refs?: string[];
};

export type PoLine = {
  for_job_ref?: string | null;
  description: string; colour: string | null; category: string | null; uom: string | null; packs: number | null;
  ordered: number | null; unit_cost: number | null; line_total: number | null; to_inventory: boolean;
  verification: PoVerification;
};

export type PoDetail = PoListRow & {
  evidence:{id:string;kind:string;value:string;channel:string;official:boolean;mailbox:string|null;message_id:string|null;attachment_name:string|null;source_url:string|null;note:string;supersedes:string|null;created_at:string}[];
  native?:{id:string;version:number;to_email:string|null;cc_email:string[];deliver_by:string|null;delivery_intent:'required_by'|'asap'|'hold';notes:string|null;raw_lines:Record<string,unknown>[]};
  lines_detail: PoLine[]; gst: number | null; total_inc_gst: number | null;
  supplier_email: string | null; credit_terms: boolean | null; deliver_to: string | null;
  install_days: string[]; next_install: string | null;
};

export async function loadPurchaseOrders(opts: {jobRef?: string; q?: string; status?: 'open' | 'all'} = {}): Promise<PoListRow[]> {
  const search = new URLSearchParams();
  if (opts.jobRef) search.set('job_ref', opts.jobRef);
  if (opts.q) search.set('q', opts.q);
  if (opts.status === 'all') search.set('status', 'all');
  const r = await fetchImpl(`${env.apiBase}/api/purchase-orders${search.size ? `?${search}` : ''}`, {headers: await authHeaders()});
  const body = await r.json();
  if (!r.ok) throw Error(body.error ?? 'Purchase orders unavailable');
  return (body.data ?? []) as PoListRow[];
}

export async function loadPurchaseOrder(po: string): Promise<PoDetail> {
  const r = await fetchImpl(`${env.apiBase}/api/purchase-orders/${encodeURIComponent(po)}`, {headers: await authHeaders()});
  const body = await r.json();
  if (!r.ok) throw Error(body.error ?? 'Purchase order unavailable');
  return body.data as PoDetail;
}

export async function officeRead<T>(path:string):Promise<T>{
 const r=await fetchImpl(`${env.apiBase}/api/po-office/${path}`,{headers:await authHeaders()});const body=await r.json();if(!r.ok)throw Error(body.error??'PO Office unavailable');return body.data;
}

/** How a new Job order starts — lead working days, pickup wording, the warehouse — from config. A failed
 *  read falls back to the shipped defaults, which are the same values Settings starts from. */
export type OrderDefaults=typeof PurchaseOrderDefaults.defaults;
export async function loadOrderDefaults():Promise<OrderDefaults>{
 const r=await fetchImpl(`${env.apiBase}/api/po-office/order-defaults`,{headers:await authHeaders()});
 if(!r.ok)return PurchaseOrderDefaults.defaults;
 const parsed=PurchaseOrderDefaults.schema.safeParse((await r.json())?.body);
 return parsed.success?parsed.data as OrderDefaults:PurchaseOrderDefaults.defaults;
}

/** The exact supplier PDF for a saved order, drawn by EmmA's delivery renderer before the send click
 *  (Jacky, 13 Oct 2026: "the PDF preview for PO must always made available for me to see before approval"). */
export async function loadPoPreview(poId: string): Promise<Blob> {
  const r = await fetchImpl(`${env.apiBase}/api/purchase-orders/${encodeURIComponent(poId)}/preview.pdf`, {headers: await authHeaders()});
  if (!r.ok || r.headers.get('content-type') !== 'application/pdf') {
    const body = await r.json().catch(() => ({}));
    throw Error((body as {error?: string}).error ?? 'PDF preview unavailable');
  }
  return r.blob();
}
